import { Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { ShieldCheck, Compass, FileText, Building2, Home as HomeIcon, Wallet, Globe2, Briefcase, ArrowRight } from "lucide-react";

const SERVICES = [
  { icon: ShieldCheck, t: "Land Verification", d: "Ownership review, site inspection, land status checks, title review, risk assessment. Walk-through report delivered." },
  { icon: Compass,     t: "Land Surveying",    d: "Boundary opening, mapping, site planning, subdivision support, and demarcation by licensed surveyors." },
  { icon: FileText,    t: "Title Processing",  d: "Verification, transfers, registration, subdivisions, and full documentation handled by our legal partners." },
  { icon: Building2,   t: "Construction",      d: "Homes, rentals, apartments, warehouses, schools, hotels, hospitals — concept to handover." },
  { icon: HomeIcon,    t: "Property Management", d: "Tenant placement, rent collection, maintenance, inspections — including diaspora owners." },
  { icon: Wallet,      t: "Financing",         d: "Mortgages, construction loans, and property financing via our banking partners." },
  { icon: Globe2,      t: "Diaspora Services", d: "Remote buying, inspections by video, construction monitoring, title processing, and management." },
  { icon: Briefcase,   t: "Legal Partners",    d: "Lawyers, valuers, surveyors, contractors, and financing institutions — all vetted." },
];

const ROADMAP = [
  { y: "Year 1", t: "50 agents · 1,000 verified listings · 100 sales · 500 monthly inquiries" },
  { y: "Year 2", t: "200 agents · 5,000 listings · national district coverage · bank partnerships" },
  { y: "Year 3", t: "500 agents · 20,000 listings · mobile app · property alerts · commercial deals" },
  { y: "Year 5", t: "50,000+ listings · 100,000 buyers · national brand · major property transactions" },
];

export default function Services() {
  return (
    <main data-testid="services-page">
      <section className="max-w-7xl mx-auto px-6 md:px-12 py-16">
        <div className="grid md:grid-cols-12 gap-10 items-end">
          <div className="md:col-span-7">
            <div className="eyebrow mb-3">Professional Services</div>
            <h1 className="font-display text-4xl md:text-6xl tracking-tighter leading-[1.05]">
              The full property stack — beyond listings.
            </h1>
            <p className="mt-5 text-muted-foreground max-w-xl text-lg leading-relaxed">
              From land verification and surveying to title processing, construction, financing, and diaspora support — TIGAWA&apos;s network handles every step.
            </p>
            <Button asChild className="rounded-full bg-primary hover:bg-primary/90 mt-7 h-12 px-7" data-testid="services-contact-btn">
              <Link to="/contact">Talk to a specialist <ArrowRight className="w-4 h-4" /></Link>
            </Button>
          </div>
          <div className="md:col-span-5">
            <div className="aspect-[4/5] rounded-3xl overflow-hidden border border-border">
              <img src="https://images.unsplash.com/photo-1682663810771-89d21838530f?crop=entropy&cs=srgb&fm=jpg&w=1200&q=85" alt="Surveying" className="w-full h-full object-cover" />
            </div>
          </div>
        </div>
      </section>

      <section className="bg-muted/40">
        <div className="max-w-7xl mx-auto px-6 md:px-12 py-20 grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {SERVICES.map((s) => (
            <div key={s.t} className="bg-card border border-border rounded-2xl p-6 hover-lift" data-testid={`svc-${s.t.toLowerCase().replace(/\s/g, '-')}`}>
              <s.icon className="w-7 h-7 text-primary" strokeWidth={1.5} />
              <h3 className="font-display text-xl font-semibold mt-5">{s.t}</h3>
              <p className="text-sm text-muted-foreground mt-2 leading-relaxed">{s.d}</p>
            </div>
          ))}
        </div>
      </section>

      <section className="max-w-7xl mx-auto px-6 md:px-12 py-24">
        <div className="text-center mb-12">
          <div className="eyebrow mb-3">Growth Roadmap</div>
          <h2 className="font-display text-3xl md:text-5xl tracking-tight">Building Uganda&apos;s largest property network.</h2>
        </div>
        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-5">
          {ROADMAP.map((r, i) => (
            <div key={r.y} className="bg-secondary text-secondary-foreground rounded-2xl p-7 hover-lift" data-testid={`roadmap-${i}`}>
              <div className="font-mono text-xs text-accent mb-3">0{i + 1}</div>
              <h3 className="font-display text-2xl font-semibold">{r.y}</h3>
              <p className="text-sm text-secondary-foreground/80 mt-3 leading-relaxed">{r.t}</p>
            </div>
          ))}
        </div>
      </section>
    </main>
  );
}
